import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, FileText, ArrowLeftCircle } from 'lucide-react'
import { AssignmentForm, AssignmentFormSubmitPayload, CourseOption } from '@/components/assignments/AssignmentForm'
import { useCreateAssignment } from '@/hooks/useAssignments'
import { useCourses } from '@/hooks/useCourses'
import { useNotifications } from '@/contexts/NotificationContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { paths } from '@/routes/path'

export function CreateAssignmentPage() {
  const navigate = useNavigate()
  const { addNotification } = useNotifications()
  const createAssignment = useCreateAssignment()

  const { data: coursesResponse, isLoading: coursesLoading, error: coursesError } = useCourses({
    limit: 100,
    isActive: true,
  })

  const courseOptions: CourseOption[] = useMemo(() => {
    if (!coursesResponse?.data) return []
    return coursesResponse.data.map((course) => ({
      id: course.id,
      label: course.title,
      subtitle: course.departmentName ?? course.programmeTitle ?? undefined,
    }))
  }, [coursesResponse])

  const handleSubmit = async (payload: AssignmentFormSubmitPayload) => {
    const { filesToRemove: _filesToRemove, files, ...rest } = payload
    const result = await createAssignment.mutateAsync({
      ...rest,
      files,
    })

    if (result) {
      addNotification({
        title: 'Assignment Created',
        message: `"${result.title}" is now available to learners.`,
        type: 'course_update',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
      navigate(paths.app.assignments.root.absolute)
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3 text-sm font-medium text-red-600">
            <FileText className="h-4 w-4" />
            Assignments
          </div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">Create a new assignment</h1>
          <p className="mt-2 text-gray-600">
            Publish assignments with clear expectations, organized resources, and smart submission rules.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(paths.app.assignments.root.absolute)}
          className="flex items-center gap-2"
        >
          <ArrowLeftCircle className="h-4 w-4" />
          Back to assignments
        </Button>
      </div>

      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <BookOpen className="h-5 w-5 text-primary" />
            Prepare your assignment
          </CardTitle>
          <CardDescription>
            Connect the assignment to a course, set its deadline, upload resources, and fine-tune submission rules.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {coursesLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : coursesError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-6 text-sm text-red-600">
              We could not load courses right now. Please refresh and try again.
            </div>
          ) : courseOptions.length === 0 ? (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-6 text-sm text-yellow-700">
              You do not have any active courses yet. Create a course first so you can attach assignments to it.
            </div>
          ) : (
            <AssignmentForm
              courses={courseOptions}
              onSubmit={handleSubmit}
              isSubmitting={createAssignment.isPending}
              submitLabel="Create assignment"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CreateAssignmentPage
